import { useEffect, useState, useCallback, useRef } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { JiraProject, JiraProjects } from '../__types__/apiUtils';
import type { IssueType } from '../providers/BulkCreateContextProvider';
import {
	getPersistedSingleWorkCreateJiraConfig,
	saveCloudIdToLocalStorage,
	saveSingleWorkCreateJiraConfigToLocalStorage,
} from '../utils/localStorageHelpers';

import { useGetAvailableSitesForSingleIssueCreateForm } from './useGetAvailableSitesForSingleIssueCreateForm';
import { useGetJiraProjectsForSingleIssueCreateForm } from './useGetJiraProjectsForSingleIssueCreateForm';
import { useGetIssueTypesForSingleIssueCreateForm } from './useGetIssueTypesForSingleIssueCreateForm';

type SitePickerData = {
	availableSites: AvailableSite[];
	selectedSite: AvailableSite | null;
	isSitePickerLoading: boolean;
	error: Error | null;
};

type ProjectPickerData = {
	selectedProject: JiraProject | null;
	optionsRecentProjects: JiraProjects;
	optionsAllProjects: JiraProjects;
	isProjectPickerLoading: boolean;
	error: Error | null;
};

type IssueTypePickerData = {
	selectedIssueType: IssueType | null;
	optionsIssueTypes: IssueType[];
	isIssueTypePickerLoading: boolean;
	error: Error | null;
};

export type SingleIssueCreateInitialData = SitePickerData &
	ProjectPickerData &
	IssueTypePickerData & {
		error: Error | null;
		onSiteChange: (site: AvailableSite | null) => void;
		onProjectChange: (project: JiraProject | null) => void;
		onIssueTypeChange: (issueType: IssueType | null) => void;
		onProjectSelectorQueryChange: (query: string) => void;
	};

export const useGetInitialDataForSingleIssueCreateForm = (): SingleIssueCreateInitialData => {
	// Track if this is the first load to apply localStorage values
	const isFirstLoadRef = useRef(true);
	const userHasMadeChangesRef = useRef(false);

	// Load localStorage once at the top
	const persistedConfig = useRef(getPersistedSingleWorkCreateJiraConfig());

	const [selectedSite, setSelectedSite] = useState<AvailableSite | null>(
		persistedConfig.current?.selectedSite || null,
	);
	const [selectedProject, setSelectedProject] = useState<JiraProject | null>(
		persistedConfig.current?.selectedProject || null,
	);
	const [projectSelectorQuery, setProjectSelectorQuery] = useState<string | null>(null);
	const [managedSelectedIssueType, setManagedSelectedIssueType] = useState<IssueType | null>(
		persistedConfig.current?.selectedIssueType || null,
	);

	// Site picker
	const {
		availableSites = [],
		selectedAvailableSite,
		isLoading: isSitePickerLoading = false,
		error: sitesError = null,
	} = useGetAvailableSitesForSingleIssueCreateForm(
		{},
		persistedConfig.current?.selectedSite || null,
	);

	useEffect(() => {
		if (selectedAvailableSite) {
			setSelectedSite(selectedAvailableSite);
		}
	}, [selectedAvailableSite]);

	// Project picker
	const {
		optionsRecentProjects,
		optionsAllProjects,
		selectedProject: projectFromHook,
		isLoading: isProjectPickerLoading,
		error: projectsError,
		onProjectChange: handleProjectChangeFromChildHook,
	} = useGetJiraProjectsForSingleIssueCreateForm(
		selectedSite,
		projectSelectorQuery,
		persistedConfig.current?.selectedProject || null,
	);

	useEffect(() => {
		if (projectFromHook) {
			setSelectedProject(projectFromHook);
		}
	}, [projectFromHook]);

	// Issue type picker (child hook)
	const {
		optionsIssueTypes,
		selectedIssueType: issueTypeFromChildHook,
		isLoading: isIssueTypePickerLoading,
		error: issueTypesError,
	} = useGetIssueTypesForSingleIssueCreateForm(
		selectedSite,
		selectedProject,
		persistedConfig.current?.selectedIssueType || null,
	);
	// Sync managedSelectedIssueType with the default/auto-selected one from child hook
	useEffect(() => {
		setManagedSelectedIssueType(issueTypeFromChildHook);
	}, [issueTypeFromChildHook]);

	// Handler for site changes by the user
	const handleSiteChange = useCallback(
		(site: AvailableSite | null) => {
			userHasMadeChangesRef.current = true;
			setSelectedSite(site);
			setSelectedProject(null); // Reset project
			setManagedSelectedIssueType(null); // Reset issue type
			setProjectSelectorQuery(null);
		},
		[setSelectedSite, setSelectedProject, setManagedSelectedIssueType, setProjectSelectorQuery],
	);

	// Handler for project changes by the user
	const handleProjectChange = useCallback(
		(project: JiraProject | null) => {
			userHasMadeChangesRef.current = true;
			setSelectedProject(project);
			setManagedSelectedIssueType(null);
			if (handleProjectChangeFromChildHook) {
				handleProjectChangeFromChildHook(project);
			}
		},
		[setSelectedProject, setManagedSelectedIssueType, handleProjectChangeFromChildHook],
	);

	// Handler for issue type changes by the user
	const handleIssueTypeChange = useCallback(
		(issueType: IssueType | null) => {
			userHasMadeChangesRef.current = true;
			setManagedSelectedIssueType(issueType);
		},
		[setManagedSelectedIssueType],
	);

	const handleProjectSelectorQueryChange = useCallback(
		(query: string) => {
			setProjectSelectorQuery(query);
		},
		[setProjectSelectorQuery],
	);

	// Save to localStorage whenever selections change (only after user makes changes)
	useEffect(() => {
		if (
			userHasMadeChangesRef.current &&
			selectedSite &&
			selectedProject &&
			managedSelectedIssueType
		) {
			// we probably should debounce this
			saveSingleWorkCreateJiraConfigToLocalStorage({
				selectedSite,
				selectedProject,
				selectedIssueType: managedSelectedIssueType,
			});
			// also save the cloud id to localStorage
			selectedSite?.cloudId && saveCloudIdToLocalStorage({ selectedCloudId: selectedSite.cloudId });
		}
	}, [selectedSite, selectedProject, managedSelectedIssueType]);

	// Mark that first load is complete after initial effects have run
	useEffect(() => {
		isFirstLoadRef.current = false;
	}, []); // Runs only once after mount

	const singleIssueCreateInitialData: SingleIssueCreateInitialData = {
		availableSites,
		selectedSite,
		isSitePickerLoading,
		selectedProject,
		optionsRecentProjects,
		optionsAllProjects,
		isProjectPickerLoading,
		optionsIssueTypes,
		selectedIssueType: managedSelectedIssueType,
		isIssueTypePickerLoading,
		error: sitesError || projectsError || issueTypesError,
		onSiteChange: handleSiteChange,
		onProjectChange: handleProjectChange,
		onIssueTypeChange: handleIssueTypeChange,
		onProjectSelectorQueryChange: handleProjectSelectorQueryChange,
	};

	return singleIssueCreateInitialData;
};
